import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import hesychiaMark from '../../assets/brand/hesychia-mark.png';
import GetStarted from '../../components/PublicSite/GetStarted';
import PublicSiteLayout from '../../components/PublicSite/PublicSiteLayout';
import UsageCounts from '../../components/PublicSite/UsageCounts';
import { publicAccent, publicCard, publicLine, publicText } from '../../components/PublicSite/publicColors';
import { HOME } from '../../utils/pageProse';

const Hero = styled.header`
  max-width: 40rem;
`;

const Mark = styled.img`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.15;
  margin: 1.25rem 0 0.75rem;
  font-weight: 700;
`;

const Lede = styled.p`
  font-size: 1.25rem;
  line-height: 1.45;
  margin: 0 0 0.75rem;
  color: ${publicText};
`;

const Memory = styled.p`
  font-size: 1.05rem;
  line-height: 1.5;
  margin: 0 0 1.5rem;
  color: ${publicText};
  opacity: 0.85;
`;

const Privacy = styled.section`
  margin-top: 1.75rem;
  max-width: 40rem;
`;

const PrivacyList = styled.ul`
  margin: 0.5rem 0 0.75rem;
  padding-left: 1.2rem;
  color: ${publicText};
  opacity: 0.85;
  line-height: 1.5;
`;

const TextLink = styled(Link)`
  color: ${publicAccent};
`;

const Features = styled.section`
  margin-top: 3rem;
  display: grid;
  gap: 1rem;
`;

const Feature = styled.article`
  border: 1px solid ${publicLine};
  border-radius: 0.75rem;
  padding: 1.25rem 1.35rem;
  background: ${publicCard};
`;

const FeatureTitle = styled.h2`
  font-size: 1.15rem;
  margin: 0 0 0.4rem;
`;

const FeatureBody = styled.p`
  margin: 0;
  color: ${publicText};
  opacity: 0.85;
  line-height: 1.5;
`;

const PriceLock = styled.p`
  font-size: 1.05rem;
  line-height: 1.5;
  margin: 0 0 1.5rem;
  color: ${publicText};
  font-weight: 600;
`;

const Roadmap = styled.section`
  margin-top: 1rem;
  border: 1px solid ${publicLine};
  border-radius: 0.75rem;
  padding: 1.25rem 1.35rem;
  background: ${publicCard};
  display: grid;
  gap: 0.75rem;
`;

const RoadmapTitle = styled.h2`
  font-size: 1.15rem;
  margin: 0;
`;

const LandingPage = (): JSX.Element => {
  return (
    <PublicSiteLayout>
      <Hero>
        <Mark src={hesychiaMark} alt="Hesychia logo" />
        <Title>Hesychia is a calm AI assistant.</Title>
        <Lede>{HOME.lede}</Lede>
        <Memory>{HOME.memory}</Memory>
        <PriceLock>{HOME.earlyAdopter}</PriceLock>
        <GetStarted source="landing" />
        <Privacy aria-label="Privacy">
          <h2>What we do with your data</h2>
          <PrivacyList>
            <li>{HOME.stored}</li>
            <li>{HOME.notSold}</li>
            <li>{HOME.cancel}</li>
          </PrivacyList>
          <TextLink to="/privacy/">Privacy details</TextLink>
        </Privacy>
        <UsageCounts />
      </Hero>
      <Features id="product" aria-label="Product">
        <Feature>
          <FeatureTitle>Memory that stays with the work</FeatureTitle>
          <FeatureBody>{HOME.memoryFeature}</FeatureBody>
        </Feature>
        <Feature>
          <FeatureTitle>Your documents, stored and searchable</FeatureTitle>
          <FeatureBody>{HOME.documentsFeature}</FeatureBody>
        </Feature>
        <Feature>
          <FeatureTitle>Business accounts</FeatureTitle>
          <FeatureBody>{HOME.businessFeature}</FeatureBody>
        </Feature>
        <Roadmap aria-labelledby="roadmap-title">
          <RoadmapTitle id="roadmap-title">Roadmap</RoadmapTitle>
          <FeatureBody>{HOME.roadmapImage}</FeatureBody>
          <FeatureBody>{HOME.roadmapVision}</FeatureBody>
        </Roadmap>
      </Features>
    </PublicSiteLayout>
  );
};

export default LandingPage;
